import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Loader from "layouts/loader/Loader";
import NontAuthorized401 from "NontAuthorized401";
import Pagination from "components/pagination";
import { Toaster, toast } from "react-hot-toast";
import { apiCall } from "utils/apiClient";

const STATUS_FILTER_OPTIONS = [
  { label: "All Status", value: "" },
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" },
  { label: "Paid", value: "paid" },
];

const STATUS_BADGES = {
  pending: "bg-yellow-100 text-yellow-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
  paid: "bg-blue-100 text-blue-700",
};

const formatDateTime = (value) => {
  if (!value) return "-";

  const parsed = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(parsed.getTime())) return String(value);

  return parsed.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatAmount = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return value || "-";

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
};

function ChannelPartnerWithdrawalRequests() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialChannelPartnerId = location.state?.channelPartnerId;
  const initialChannelPartnerName = location.state?.channelPartnerName;

  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [status, setStatus] = useState("");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [nextStatus, setNextStatus] = useState("");
  const [note, setNote] = useState("");
  const [rejectedReason, setRejectedReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchWithdrawalRequests = async () => {
    setLoading(true);
    setLoadError("");

    const result = await apiCall({
      endpoint: "admin/channel-partner/wallet/withdraw-requests",
      method: "GET",
      params: {
        page: String(currentPage),
        limit: String(recordsPerPage),
        ...(status ? { status } : {}),
        ...(initialChannelPartnerId ? { channel_partner_id: String(initialChannelPartnerId) } : {}),
      },
    });

    if (result?.status === 200) {
      setRequests(Array.isArray(result.data) ? result.data : []);
      setTotalRecords(result.pagination?.total_records || 0);
      setTotalPages(result.pagination?.total_pages || 0);
      setLoading(false);
      return;
    }

    setRequests([]);
    setTotalRecords(0);
    setTotalPages(0);
    setLoadError(result?.message || "Unable to load channel partner withdrawal requests.");
    setLoading(false);
  };

  useEffect(() => {
    fetchWithdrawalRequests();
  }, [currentPage, recordsPerPage, status, initialChannelPartnerId]);

  const heading = initialChannelPartnerId
    ? `${initialChannelPartnerName || "Channel Partner"} Withdrawal Requests`
    : "All Channel Partner Withdrawal Requests";

  const emptyStateMessage = useMemo(() => {
    if (loadError) {
      return loadError;
    }

    return "No withdrawal requests found.";
  }, [loadError]);

  const openActionModal = (request, statusValue) => {
    setSelectedRequest(request);
    setNextStatus(statusValue);
    setNote("");
    setRejectedReason("");
    setActionModalOpen(true);
  };

  const closeActionModal = () => {
    setActionModalOpen(false);
    setSelectedRequest(null);
    setNextStatus("");
    setNote("");
    setRejectedReason("");
    setSubmitting(false);
  };

  const getActionLabel = () => {
    if (nextStatus === "approved") return "Approve";
    if (nextStatus === "rejected") return "Reject";
    if (nextStatus === "paid") return "Mark as Paid";
    return "Update";
  };

  const updateWithdrawalRequestStatus = async () => {
    if (!selectedRequest?.id || !nextStatus) return;

    if (nextStatus === "rejected" && !rejectedReason.trim()) {
      toast.error("Rejected reason is required.");
      return;
    }

    setSubmitting(true);

    const payload = new URLSearchParams({
      status: nextStatus,
      approved_by_admin_id: localStorage.getItem("id") || "0",
      note,
      rejected_reason: nextStatus === "rejected" ? rejectedReason : "",
    });

    const result = await apiCall({
      endpoint: `admin/channel-partner/wallet/withdraw-requests/${selectedRequest.id}/status`,
      method: "PUT",
      data: payload,
    });

    if (result?.status === 200) {
      toast.success(result.message || "Withdrawal request updated successfully.");
      closeActionModal();
      fetchWithdrawalRequests();
      return;
    }

    setSubmitting(false);
    toast.error(result?.message || "Unable to update withdrawal request.");
  };

  return (
    <>
      {localStorage.getItem("Token") ? (
        <DashboardLayout>
          <DashboardNavbar />
          <Toaster position="top-center" reverseOrder={false} />

          {loading ? (
            <>
              <div className="relative bg-white h-screen overflow-hidden" />
              <div className="flex justify-center">
                <div className="absolute top-[30%]">
                  <Loader />
                </div>
              </div>
            </>
          ) : (
            <div className="mt-6 px-4 sm:px-6">
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <button
                      onClick={() =>
                        navigate(initialChannelPartnerId ? "/channel-partner-details" : "/channel-partner", {
                          state: initialChannelPartnerId
                            ? {
                                partner: {
                                  id: initialChannelPartnerId,
                                  name: initialChannelPartnerName,
                                },
                              }
                            : undefined,
                        })
                      }
                      className="mb-2 text-sm font-medium text-blue-600 hover:underline"
                    >
                      {initialChannelPartnerId ? "Back to Channel Partner Details" : "Back to Channel Partner List"}
                    </button>
                    <div className="text-lg font-semibold text-gray-900">{heading}</div>
                    <div className="text-xs text-gray-500">
                      Showing {requests.length} of {totalRecords}
                    </div>
                  </div>
                </div>

                {loadError && (
                  <div className="mx-4 mt-4 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
                    {loadError}
                  </div>
                )}

                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-3 xl:grid-cols-4">
                    <select
                      value={status}
                      onChange={(event) => {
                        setStatus(event.target.value);
                        setCurrentPage(1);
                      }}
                      className="h-9 w-full text-sm bg-white border border-gray-300 rounded-md px-2 focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
                    >
                      {STATUS_FILTER_OPTIONS.map((option) => (
                        <option key={option.value || "all-status"} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>

                    <div className="md:col-span-2 xl:col-span-3 flex items-center justify-end">
                      <button
                        onClick={() => {
                          setCurrentPage(1);
                          setStatus("");
                        }}
                        className="text-xs px-3 h-8 rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm text-left text-gray-700">
                    <thead className="text-[11px] text-gray-500 uppercase bg-gray-50 tracking-wide">
                      <tr className="border-b border-gray-200">
                        <th className="py-2 px-3 whitespace-nowrap">Sr No</th>
                        <th className="py-2 px-3 whitespace-nowrap">Request ID</th>
                        <th className="py-2 px-3 whitespace-nowrap">Channel Partner</th>
                        <th className="py-2 px-3 whitespace-nowrap text-right">Amount</th>
                        <th className="py-2 px-3 whitespace-nowrap">Status</th>
                        <th className="py-2 px-3 whitespace-nowrap">Method</th>
                        <th className="py-2 px-3 whitespace-nowrap">Reference</th>
                        <th className="py-2 px-3 whitespace-nowrap">Created At</th>
                        <th className="py-2 px-3 whitespace-nowrap">Updated At</th>
                        <th className="py-2 px-3 whitespace-nowrap">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requests.length > 0 ? (
                        requests.map((request, index) => (
                          <tr
                            key={request.id || `${request.channel_partner_id || "request"}-${index}`}
                            className="bg-white border-b border-gray-100 hover:bg-gray-50"
                          >
                            <td className="py-2 px-3 whitespace-nowrap">
                              {(currentPage - 1) * recordsPerPage + index + 1}
                            </td>
                            <td className="py-2 px-3 whitespace-nowrap font-medium text-gray-900">
                              {request.id || "-"}
                            </td>
                            <td className="py-2 px-3 min-w-[220px]">
                              <div className="font-medium text-gray-900">
                                {request.channel_partner_name || initialChannelPartnerName || "-"}
                              </div>
                              <div className="text-xs text-gray-500">
                                ID: {request.channel_partner_id || initialChannelPartnerId || "-"}
                              </div>
                            </td>
                            <td className="py-2 px-3 whitespace-nowrap text-right">
                              {formatAmount(request.amount)}
                            </td>
                            <td className="py-2 px-3 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                  STATUS_BADGES[request.status] || "bg-gray-100 text-gray-700"
                                }`}
                              >
                                {request.status || "-"}
                              </span>
                            </td>
                            <td className="py-2 px-3 whitespace-nowrap">
                              {request.method || request.payment_method || "-"}
                            </td>
                            <td className="py-2 px-3 whitespace-nowrap">
                              {request.reference || request.reference_no || request.transaction_id || "-"}
                            </td>
                            <td className="py-2 px-3 whitespace-nowrap">{formatDateTime(request.created_at)}</td>
                            <td className="py-2 px-3 whitespace-nowrap">{formatDateTime(request.updated_at)}</td>
                            <td className="py-2 px-3 whitespace-nowrap">
                              <div className="flex flex-wrap gap-2">
                                {request.status === "pending" && (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => openActionModal(request, "approved")}
                                      className="rounded-md bg-green-600 px-3 py-1 text-xs font-medium text-white hover:bg-green-700"
                                    >
                                      Approve
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => openActionModal(request, "rejected")}
                                      className="rounded-md bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700"
                                    >
                                      Reject
                                    </button>
                                  </>
                                )}
                                {request.status === "approved" && (
                                  <button
                                    type="button"
                                    onClick={() => openActionModal(request, "paid")}
                                    className="rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700"
                                  >
                                    Mark Paid
                                  </button>
                                )}
                                {!["pending", "approved"].includes(request.status) && (
                                  <span className="text-xs font-medium text-gray-500">No actions</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={10} className="py-6 text-center text-gray-500">
                            {emptyStateMessage}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="px-2 py-2 border-t border-gray-200">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => setCurrentPage(page)}
                    recordsPerPage={recordsPerPage}
                    onRecordsPerPageChange={(value) => {
                      setCurrentPage(1);
                      setRecordsPerPage(value);
                    }}
                    totalRecords={totalRecords}
                    count={requests.length}
                    showFirstLastControls
                    compact
                  />
                </div>
              </div>
            </div>
          )}

          <Modal
            title={`${getActionLabel()} Withdrawal Request`}
            open={actionModalOpen}
            onCancel={closeActionModal}
            onOk={updateWithdrawalRequestStatus}
            okText={submitting ? "Saving..." : getActionLabel()}
            okButtonProps={{ disabled: submitting }}
            cancelButtonProps={{ disabled: submitting }}
          >
            <div className="space-y-4">
              <div className="text-sm text-gray-600">
                Request ID: <span className="font-medium text-gray-900">{selectedRequest?.id || "-"}</span>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Note</label>
                <textarea
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  rows={3}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="Add an internal note"
                />
              </div>

              {nextStatus === "rejected" && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Rejected Reason</label>
                  <textarea
                    value={rejectedReason}
                    onChange={(event) => setRejectedReason(event.target.value)}
                    rows={3}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                    placeholder="Enter rejection reason"
                  />
                </div>
              )}
            </div>
          </Modal>
        </DashboardLayout>
      ) : (
        <NontAuthorized401 />
      )}
    </>
  );
}

export default ChannelPartnerWithdrawalRequests;
